import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import {
  CurrentEventQuery,
  FolkloreType,
} from '../../../../../apollo/__generated__/graphql';
import { useToast } from '../../../../../toast/Toast.context';
import { useCurrentUserQuery } from '../../../../../user/hooks/useCurrentUserQuery';
import { useCurrentMilestone } from '../../../../hooks/useCurrentMilestone';
import { useEventUsersQuery } from '../../../../hooks/useEventUsersQuery';
import { useJoinEventMutation } from '../../../../hooks/useJoinEventMutation';

interface EventRegisterToEventFormInputs {
  hasValidatedTerms: boolean;
  comment?: string;
  referentId?: string;
  referentFirstname?: string;
  referentLastname?: string;
  referentPhone?: string;
}

const schema = yup
  .object({
    hasValidatedTerms: yup
      .boolean()
      .required("Vous devez accepter l'attestation sur l'honneur")
      .oneOf([true], "Vous devez accepter l'attestation sur l'honneur"),
    comment: yup.string().optional(),
    referentId: yup.string().optional(),
    referentFirstname: yup.string().optional(),
    referentLastname: yup.string().optional(),
    referentPhone: yup.string().optional(),
  })
  .required();

export const useEventInformationCard = (
  currentEvent: NonNullable<CurrentEventQuery['currentEvent']>,
) => {
  const currentMilestone = useCurrentMilestone(currentEvent.milestones);
  const { data: eventUsersData } = useEventUsersQuery(currentEvent.id);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { data: currentUserData } = useCurrentUserQuery();
  const {
    register,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<EventRegisterToEventFormInputs>({
    resolver: yupResolver(schema),
  });

  const { joinEvent } = useJoinEventMutation(() => {
    navigate('/dashboard');
    showToast({
      type: 'success',
      message: "Vous avez rejoint l'événement",
    });
  });

  const onValid: SubmitHandler<EventRegisterToEventFormInputs> = async (
    data,
  ) => {
    if (data.referentId) {
      joinEvent({
        eventId: currentEvent.id,
        comment: data.comment,
        internalReferentId: data.referentId,
      });
    } else {
      joinEvent({
        eventId: currentEvent.id,
        comment: data.comment,
        internalReferentFirstname: data.referentFirstname,
        internalReferentLastname: data.referentLastname,
        internalReferentPhone: data.referentPhone,
      });
    }
  };

  const onSubmit = handleSubmit(onValid);

  const currentUser = currentUserData?.currentUser;

  const users = useMemo(
    () =>
      (eventUsersData?.eventUsers ?? []).filter((user) => {
        if (user.id === currentUser?.id) {
          return false;
        }
        if (!user.folklore) {
          return false;
        }

        if (
          user.folklore !== FolkloreType.Faluchard &&
          user.folklore !== FolkloreType.Other
        ) {
          return false;
        }

        return true;
      }),
    [eventUsersData, currentUser],
  );

  const referentId = getValues('referentId');
  const onReferentChange = (value: string | null) => {
    setValue('referentId', value ?? undefined, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return {
    onSubmit,
    register,
    errors,
    currentUser,
    currentMilestone,
    users,
    referentId,
    onReferentChange,
  };
};
